/* eslint-disable max-len */
import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/store/app.reducer';
import { Observable, Subscription } from 'rxjs';
import { FormControl } from '@angular/forms';
import { PaginationParams } from 'src/app/model/api/pagination-params';
import { debounceTime, filter, map } from 'rxjs/operators';
import * as DeviceActions from 'src/app/store/device/device.actions';
import { TranslateService } from '@ngx-translate/core';
import { Site } from 'src/app/model/site';
import { TIMESLICE_DEFAULT_SORT_COLUMN, TIMESLICE_DEFAULT_SORT_COLUMN_DIRECTION, TIMESLICE_PAGE_DEFAULT_SIZE, TIMESLICE_PAGE_SIZE_OPTIONS, TIMESLICE_TABLE_HEADER } from 'src/app/conf/mtma-rules-access';
import { DatePipe } from '@angular/common';
import moment from 'moment';
import { selectCurrentSite } from 'src/app/store/site/site.selector';
import { selectMtmaActiveTask, selectMtmaTimesliceModify, selectMtmaTimeslices, selectMtmaTimeslicesMaxData, selectMtmaTimeslicesPagination } from 'src/app/store/device/device.selector';
import { ItemsPagination, MtmaMaxData } from 'src/app/model/api/data-rest';
import { MtmaTask } from 'src/app/model/mtma-task';
import { TableLazyLoadEvent } from 'primeng/table';
import { pageSortChange } from 'src/app/services/table';
import { ConfirmationService } from 'primeng/api';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { MtmaTimeSliceDialogComponent } from '../mtma-time-slice-dialog/mtma-time-slice-dialog.component';
import { StandardDesignParameters, StandardMtmaLayout, StandardPageLayout } from 'src/app/modules/shared/stdlayoutlib/classes.stdlayoutlib';
import { selectDeviceTabIndex } from 'src/app/store/home/home.selector';
import { MtmaTimeslice } from 'src/app/model/mtma-rules-access';
import { FlagTimeslice } from 'src/app/conf/flag-timeslices';


@Component({
	selector: 'app-mtma-rules-access-timeslices',
	templateUrl: './mtma-rules-access-timeslices.component.html',
	providers: [ConfirmationService, DialogService],
	styles: `
		:host ::ng-deep .p-panel-header {
			border: 1px solid #D5D5D5 !important;
			border-bottom: none !important;
		}
		:host ::ng-deep .p-panel-content {
			border: 1px solid #D5D5D5 !important;
		}
	`
})

export class MtmaRulesAccessTimeslicesComponent implements OnInit, OnDestroy {
	site$: Observable<Site | undefined>;
	siteSub: Subscription | undefined;
	mtmaTimeslices$: Observable<MtmaTimeslice[] | undefined>;
	mtmaTimeslicesSub: Subscription | undefined;
	mtmaTimeslicesPagination$: Observable<ItemsPagination | undefined>;
	mtmaTimeslicesPaginationSub: Subscription | undefined;
	mtmaTimeslicesMaxData$: Observable<MtmaMaxData | undefined>;
	mtmaTimeslicesMaxDataSub: Subscription | undefined;
	mtmaTimesliceModify$: Observable<boolean | undefined>;
	mtmaTimesliceModifySub: Subscription | undefined;
	mtmaActiveTask$: Observable<MtmaTask | undefined>;
	mtmaActiveTaskSub: Subscription | undefined;
	tabIndex: number;
	tabIndex$: Observable<number | undefined>;
	tabIndexSub: Subscription | undefined;

	searchFilter: FormControl = new FormControl('');
	filterSub: Subscription;
	filter: '';

	timesliceColumns = TIMESLICE_TABLE_HEADER;
	timeslicePagingSub: Subscription;
	timeslicePageSortSub: Subscription;
	timeslicePaging: PaginationParams = {
		size: TIMESLICE_PAGE_DEFAULT_SIZE,
		page: 1,
		sort: TIMESLICE_DEFAULT_SORT_COLUMN,
		order: TIMESLICE_DEFAULT_SORT_COLUMN_DIRECTION,
	};
	timeslicePageSize: number = TIMESLICE_PAGE_DEFAULT_SIZE;
	timeslicePageSizeOptions = TIMESLICE_PAGE_SIZE_OPTIONS;
	totalRecords: number;

	timesliceMaxSizeMemorySub: Subscription;
	timesliceMemorySize = 1;
	timesliceMaxMemorySize = 1;

	timesliceLoading: boolean;
	mtmaTimeslices: MtmaTimeslice[];

	currentSite: Site;

	timesliceConfirmDeleteSub: Subscription;

	dateTransform: DatePipe;
	isActiveTask = false;
	daysOfWeek = new Map<number, string>([
		[0, 'SUN'],
		[1, 'MON'],
		[2, 'TUE'],
		[3, 'WED'],
		[4, 'THU'],
		[5, 'FRI'],
		[6, 'SAT'],
	]);
	ref: DynamicDialogRef | undefined;

	acceptButtonStyle: string;
	rejectButtonStyle: string;

	screenWidth: number;
	MIN_MD_WIDTH = StandardDesignParameters.MAX_MD_WIDTH;

	constructor(
		private store: Store<AppState>,
		public confirmationService: ConfirmationService,
		private translate: TranslateService,
		public dialogService: DialogService,
	) {
		this.initSelectors();
	}

	initSelectors(): void {
		this.site$ = this.store.select(selectCurrentSite);
		this.mtmaTimeslices$ = this.store.select(selectMtmaTimeslices);
		this.mtmaTimeslicesPagination$ = this.store.select(selectMtmaTimeslicesPagination);
		this.mtmaTimeslicesMaxData$ = this.store.select(selectMtmaTimeslicesMaxData);
		this.mtmaTimesliceModify$ = this.store.select(selectMtmaTimesliceModify);
		this.mtmaActiveTask$ = this.store.select(selectMtmaActiveTask);
		this.tabIndex$ = this.store.select(selectDeviceTabIndex);
	}

	ngOnInit(): void {
		const currentLang = this.translate.currentLang;

		this.dateTransform = new DatePipe(currentLang);

		this.siteSub = this.site$.subscribe(currentSite => {
			this.currentSite = currentSite;
		});

		this.mtmaActiveTaskSub = this.mtmaActiveTask$.subscribe(mtmaActiveTask => {
			if (mtmaActiveTask.Id !== '') {
				this.isActiveTask = true;
			} else {
				this.isActiveTask = false;
			}
		});
		this.tabIndexSub = this.tabIndex$?.subscribe(
			async (tabIndex: number) => {
				this.tabIndex = tabIndex;
				this.dispatchGetTimeslices();
			});

		this.initTimeslice();
		this.onResize();
	}

	initTimeslice(): void {
		this.filterSub = this.searchFilter.valueChanges.pipe(
			debounceTime(500),
			map(v => v.trim().toLowerCase())
		).subscribe(value => {
			this.filter = value;
			this.dispatchGetTimeslices();
		});
		this.dispatchGetTimeslices();
		this.mtmaTimeslicesSub = this.mtmaTimeslices$.subscribe(mtmaTimeslices => {
			this.mtmaTimeslices = mtmaTimeslices;
			this.timesliceLoading = false;
		});

		this.timeslicePagingSub = this.mtmaTimeslicesPagination$
			.pipe(filter(p => !!p))
			.subscribe(paging => {
				this.totalRecords = paging.TotalCount;
			});

		this.timesliceMaxSizeMemorySub = this.mtmaTimeslicesMaxData$
			.pipe(filter(p => !!p))
			.subscribe(mtmaTimeslicesMaxData => {
				this.timesliceMemorySize = mtmaTimeslicesMaxData.Current;
				this.timesliceMaxMemorySize = mtmaTimeslicesMaxData.Max;
			});

		this.mtmaTimesliceModifySub = this.mtmaTimesliceModify$
			.pipe(filter(mtmaTimesliceModify => !!mtmaTimesliceModify))
			.subscribe(() => {
				this.dispatchGetTimeslices();
			});
	}

	ngOnDestroy(): void {
		this.mtmaTimesliceModifySub?.unsubscribe();
		this.siteSub?.unsubscribe();
		this.timesliceMaxSizeMemorySub?.unsubscribe();
		this.mtmaActiveTaskSub?.unsubscribe();
		this.tabIndexSub?.unsubscribe();
	}


	loadTimeslices(event: TableLazyLoadEvent) {
		this.timesliceLoading = true;

		this.timeslicePaging = pageSortChange(this.timeslicePaging, event);

		this.dispatchGetTimeslices();
	}

	dispatchGetTimeslices(): void {
		if (this.isSelectTab()) {
			this.store.dispatch(DeviceActions.getMtmaTimeSlices({
				filter: this.filter,
				sort: this.timeslicePaging.sort, order: this.timeslicePaging.order, size: this.timeslicePaging.size, page: this.timeslicePaging.page
			}));
		}
	}

	isMtmaConnect(): boolean {
		//return (this.deviceModel !== undefined && (this.deviceModel.Id === EDeviceModel.MTMACONNECT) && this.tabIndex === 9); // TAB INDEX FOR MTMACONNECT CALLLOG IS 9
		return (this.tabIndex === 8); // TAB INDEX FOR MTMACONNECT IS 8 tab only for mtma

	}

	isSelectTab(): boolean {
		return this.isMtmaConnect();
	}


	addTimeslice(): void {
		this.store.dispatch(DeviceActions.resetTimeSliceForm());
		this.ref = this.dialogService.open(MtmaTimeSliceDialogComponent, {
			header: this.translate.instant('ADD_TIMESLICE'),
			width: '80vw',
			breakpoints: { '1600px': '98vw' },
			contentStyle: { overflow: 'auto' },
		});
	}

	editTimeslice(timeslice: MtmaTimeslice): void {
		this.store.dispatch(DeviceActions.resetTimeSliceForm());
		this.ref = this.dialogService.open(MtmaTimeSliceDialogComponent, {
			header: this.translate.instant('EDIT_TIMESLICE'),
			width: '80vw',
			breakpoints: { '1600px': '98vw' },
			contentStyle: { overflow: 'auto' },
			data: {
				timeslice
			}
		});
	}

	deleteTimeslice(timeslice: MtmaTimeslice): void {
		this.acceptButtonStyle = "p-button-danger";
		this.rejectButtonStyle = "p-button-secondary";
		this.confirmationService.confirm({
			header: this.translate.instant('DELETE_TIMESLICE'),
			message: this.translate.instant('DELETE_TIMESLICE_WARNING'),
			acceptLabel: this.translate.instant('CONFIRM'),
			rejectLabel: this.translate.instant('CANCEL'),
			icon: 'pi cm-alert',
			accept: () => {
				this.store.dispatch(DeviceActions.deleteMtmaTimeSlice({ timesliceId: timeslice.Id }));
			},
			reject: () => {
			}
		});
	}

	getRepetitionTimeslice(timeslice: MtmaTimeslice): number {
		const listFlag = [];
		let value = timeslice.Flags;
		for (let i = 3; i > -1; i--) {
			if (value !== 0 && value / (2 ** i) >= 1) {
				listFlag.push(1);
			} else {
				listFlag.push(0);
			}
			value = value % (2 ** i);
		}
		if (listFlag[3 - FlagTimeslice.REPETITION_MONTHLY] === 1) {
			return FlagTimeslice.REPETITION_MONTHLY;
		} else if (listFlag[3 - FlagTimeslice.REPETITION_ANNUAL] === 1) {
			return FlagTimeslice.REPETITION_ANNUAL;
		}
		return 0;
	}

	getLabelRepetition(repetition: number): string {
		switch (repetition) {
			case 2: {   // MONTLY
				return this.translate.instant('MONTHLY');
			}
			case 3: {   // ANNUAL
				return this.translate.instant('REPETITION_ANNUAL');
			}
			default: {
				return "-";
			}
		}
	}

	getColorRepetition(repetition: number): string {
		switch (repetition) {
			case 2: {   // MONTLY
				return 'w-10rem p-tag-lightBlue0220';
			}
			case 3: {   // ANNUAL
				return 'w-10rem p-tag-lightBlue0240';
			}
			default: {
				return 'w-10rem p-tag-darkBlue10';
			}
		}
	}

	getIncludeExcludeTimeslice(timeslice: MtmaTimeslice): number {
		const listFlag = [];
		let value = timeslice.Flags;
		for (let i = 5; i > -1; i--) {
			if (value !== 0 && value / (2 ** i) >= 1) {
				listFlag.push(1);
			} else {
				listFlag.push(0);
			}
			value = value % (2 ** i);
		}
		if (listFlag[5 - FlagTimeslice.ESCLUDE_INCLUDE] === 1) {
			return 1;
		}
		return 0;
	}

	getDetailsTimeslice(timeslice: MtmaTimeslice): string {
		let detail = '';
		const momFrom = moment(timeslice.DtFrom);
		const momTo = moment(timeslice.DtTo);
		if (timeslice.Type === 1) {
			const currentWeekDays = [];
			let value = timeslice.Weekdays;
			for (let i = 6; i > -1; i--) {
				if (value !== 0 && value / (2 ** i) >= 1) {
					currentWeekDays.unshift(i);
					detail = this.translate.instant(this.daysOfWeek.get(i)) + ' ' + detail;
				}
				value = value % (2 ** i);
			}
		} else if (timeslice.Type === 2) {
			const repetition = this.getRepetitionTimeslice(timeslice);
			if (repetition === FlagTimeslice.REPETITION_MONTHLY) {
				const listFlag = [];
				let value = timeslice.Flags;
				for (let i = 6; i > -1; i--) {
					if (value !== 0 && value / (2 ** i) >= 1) {
						listFlag.push(1);
					} else {
						listFlag.push(0);
					}
					value = value % (2 ** i);
				}
				let dayMonthStart = momFrom.utc().date();
				let dayMonthEnd = momTo.utc().date();
				if (listFlag[6 - FlagTimeslice.DATE_29] + listFlag[6 - FlagTimeslice.DATE_30] + listFlag[6 - FlagTimeslice.DATE_31] > 0) {
					if (listFlag[6 - FlagTimeslice.DATE_31] === 1) {
						dayMonthEnd = 31;
					}
					if (listFlag[6 - FlagTimeslice.DATE_30] === 1 && listFlag[6 - FlagTimeslice.DATE_31] === 1) {
						dayMonthStart = 30;
					} else if (listFlag[6 - FlagTimeslice.DATE_30] === 1 && listFlag[6 - FlagTimeslice.DATE_31] === 0) {
						dayMonthEnd = 30;
					}
					if (listFlag[6 - FlagTimeslice.DATE_29] === 1 && (listFlag[6 - FlagTimeslice.DATE_31] === 1 || listFlag[6 - FlagTimeslice.DATE_30] === 1)) {
						dayMonthStart = 29;
					} else if (listFlag[6 - FlagTimeslice.DATE_29] === 1 && listFlag[6 - FlagTimeslice.DATE_31] === 0 && listFlag[6 - FlagTimeslice.DATE_30] === 0) {
						dayMonthEnd = 29;
					}
				}
				detail = detail + ' ' + this.translate.instant('DAY_FROM') + ' ' + dayMonthStart +
					' - ' + this.translate.instant('DAY_TO') + ' ' + dayMonthEnd;
			} else if (repetition === FlagTimeslice.REPETITION_ANNUAL) {
				detail = detail + ' ' + this.translate.instant('DAY_MONTH_FROM') + ' ' + momFrom.utc().format('DD/MM') +
					' - ' + this.translate.instant('DAY_MONTH_TO') + ' ' + momTo.utc().format('DD/MM');
			}

		}
		return detail;
	}

	getTimeTimeslice(timeslice: MtmaTimeslice): string {
		const momFrom = moment(timeslice.DtFrom);
		const momTo = moment(timeslice.DtTo);


		let detail = '';
		detail = detail + ' ' + this.translate.instant('DATE_FROM') + ' ' + momFrom.format('HH:mm') +
			' - ' + this.translate.instant('DATE_TO') + ' ' + momTo.format('HH:mm');

		return detail;
	}

	getTemporaryTimeslice(timeslice: MtmaTimeslice): string {
		const listFlag = [];
		const momFrom = moment(timeslice.DtFrom);
		const momTo = moment(timeslice.DtTo);
		let value = timeslice.Flags;
		for (let i = 6; i > -1; i--) {
			if (value !== 0 && value / (2 ** i) >= 1) {
				listFlag.push(1);
			} else {
				listFlag.push(0);
			}
			value = value % (2 ** i);
		}
		let detail = '-';
		if (listFlag[6 - FlagTimeslice.TEMPORARY] === 1) {
			if (timeslice.Type === 1) {
				detail = this.translate.instant('FROM') + ' ' + momFrom.format('DD/MM/yyyy') +
					' - ' + this.translate.instant('TO') + ' ' + momTo.format('DD/MM/yyyy');
			} else if (timeslice.Type === 2) {
				const repetition = this.getRepetitionTimeslice(timeslice);
				if (repetition === FlagTimeslice.REPETITION_MONTHLY) {
					detail = this.translate.instant('DAY_MONTH_FROM') + ' ' + momFrom.format('MM/yyyy') +
						' - ' + this.translate.instant('DAY_MONTH_TO') + ' ' + momTo.format('MM/yyyy');
				} else if (repetition === FlagTimeslice.REPETITION_ANNUAL) {
					detail = this.translate.instant('FROM') + ' ' + momFrom.format('yyyy') +
						' - ' + this.translate.instant('TO') + ' ' + momTo.format('yyyy');
				}

			}
		}
		return detail;
	}

	getValueMemory(): number {
		return Math.ceil((this.timesliceMemorySize / this.timesliceMaxMemorySize) * 100);
	}

	getLabelTypeTag(timeslice: MtmaTimeslice): string {
		switch (timeslice.Type) {
			case 1: {
				return this.translate.instant('TIMESLOT');
			}
			case 2: {
				return this.translate.instant('SPECIALPERIOD');
			}
			default: {
				return this.translate.instant('NO_ENTRIES');
			}
		}
	}

	getColorTypeTag(timeslice: MtmaTimeslice): string {
		switch (timeslice.Type) {
			case 1: {
				return 'w-10rem p-tag-lightBlue0220';
			}
			case 2: {
				return 'w-10rem p-tag-yellowWarning';
			}
			default: {
				return 'w-10rem p-tag-darkBlue10';
			}
		}
	}

	// Design
	@HostListener('window:resize')
	onResize() {
		this.screenWidth = window.innerWidth;
	}

	onExtendedMobile(): boolean {
		return StandardPageLayout.onExtendedMobile(this.screenWidth);
	}

	actionLayout(position: string): string {
		return StandardMtmaLayout.actionLayout(this.screenWidth, position);
	}

	layoutMarginResponsive(): string {
		return StandardPageLayout.layoutMarginResponsive(this.screenWidth);
	}

	dynamicInputTextWidth(): string {
		return StandardPageLayout.dynamicInputTextWidth(this.screenWidth, 'w-25rem');
	}

	dynamicInputSearchWidth(): string {
		return StandardMtmaLayout.dynamicInputSearchWidth(this.screenWidth, 'w-25rem');
	}
}


